import type { JSX } from "react";
import { Markdown } from "../components/chat/Markdown";
import { IconGrid, IconChart, IconGitBranch, IconFile } from "../components/icons";
import type { ReleaseSection } from "./entries";

/** Crafted monoline icons for pillar cards (premium; keyed from the entry data). */
const PILLAR_ICONS: Record<string, (p: { size?: number }) => JSX.Element> = {
  grid: IconGrid,
  chart: IconChart,
  branch: IconGitBranch,
  file: IconFile
};

/**
 * Renders a release note's structured `sections` as themed components — the
 * launch-page feel (chat-demo / pillar cards / comparison) that plain markdown
 * can't express (openspec/cyclecare-whats-new.md §6). All visuals are
 * token-based, so they adapt to the user's light/dark theme. The markup mirrors
 * the design mockup (docs/release-notes/2026-06-27-code-qa.html) so the in-app
 * render matches it.
 */
export function ReleaseSections({ sections }: { sections: ReleaseSection[] }): JSX.Element {
  return (
    <>
      {sections.map((s, i) => {
        switch (s.kind) {
          case "lead":
            return (
              <div key={i} className="rs-lead">
                <Markdown>{s.md}</Markdown>
              </div>
            );
          case "prose":
            return (
              <div key={i} className="rs-prose">
                <Markdown>{s.md}</Markdown>
              </div>
            );

          case "scene": {
            const copy = (
              <div className="rs-copy" key="copy">
                {s.copy ? <Markdown>{s.copy}</Markdown> : null}
              </div>
            );
            // Prefer a real product screenshot, shown big (Anthropic-style image
            // break). Fall back to the hand-built chat mock only when no `shot`.
            if (s.shot) {
              return (
                <section key={i} className="rs-section">
                  {s.eyebrow ? <div className="rs-eyebrow">{s.eyebrow}</div> : null}
                  <h2 className="rs-h">{s.heading}</h2>
                  {copy}
                  <figure className="rs-shot">
                    <img src={s.shot} alt="" />
                  </figure>
                </section>
              );
            }
            const reply = s.reply;
            const chat =
              s.ask && reply ? (
                <div className="rs-chat" key="chat">
                  <div className="rs-ask">{s.ask}</div>
                  <div className="rs-reply">
                    <div className="rs-av" aria-hidden="true">
                      环
                    </div>
                    <div className="rs-reply-body">
                      {reply.trace && reply.trace.length > 0 ? (
                        <div className="rs-traces">
                          {reply.trace.map((t, ti) => (
                            <span className="rs-trace" key={ti}>
                              <span className="rs-trace-ic" aria-hidden="true">
                                码
                              </span>
                              <b>{t}</b>
                            </span>
                          ))}
                        </div>
                      ) : null}
                      <Markdown>{reply.body}</Markdown>
                    </div>
                  </div>
                </div>
              ) : null;
            return (
              <section key={i} className="rs-section">
                {s.eyebrow ? <div className="rs-eyebrow">{s.eyebrow}</div> : null}
                <h2 className="rs-h">{s.heading}</h2>
                <div className="rs-scene">{s.flip ? [chat, copy] : [copy, chat]}</div>
              </section>
            );
          }

          case "pillars":
            return (
              <section key={i} className="rs-section">
                {s.eyebrow ? <div className="rs-eyebrow">{s.eyebrow}</div> : null}
                {s.heading ? <h2 className="rs-h">{s.heading}</h2> : null}
                <div className="rs-pillars">
                  {s.items.map((it, pi) => (
                    <div className="rs-pillar" key={pi}>
                      {it.icon && PILLAR_ICONS[it.icon] ? (
                        <span className="rs-pillar-i" aria-hidden="true">
                          {PILLAR_ICONS[it.icon]!({ size: 20 })}
                        </span>
                      ) : it.emoji ? (
                        <span className="rs-pillar-e" aria-hidden="true">
                          {it.emoji}
                        </span>
                      ) : null}
                      <div className="rs-pillar-h">{it.title}</div>
                      <p>{it.desc}</p>
                    </div>
                  ))}
                </div>
              </section>
            );

          case "compare":
            return (
              <section key={i} className="rs-section">
                {s.eyebrow ? <div className="rs-eyebrow">{s.eyebrow}</div> : null}
                {s.heading ? <h2 className="rs-h">{s.heading}</h2> : null}
                <table className="rs-compare">
                  <thead>
                    <tr>
                      {s.head.map((h, hi) => (
                        <th key={hi}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {s.rows.map((r, ri) => (
                      <tr key={ri}>
                        <td className="rs-k">{r[0]}</td>
                        <td className="rs-was">{r[1]}</td>
                        <td className="rs-now">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            );

          case "quote":
            return (
              <blockquote key={i} className="rs-quote">
                <Markdown>{s.md}</Markdown>
              </blockquote>
            );

          case "media":
            return <img key={i} className="rs-media-inline" src={s.src} alt={s.alt ?? ""} />;

          default:
            return null;
        }
      })}
    </>
  );
}
